import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "@/services/hooks";
import {
  getFolders,
  getPictures,
  addFolder,
  clearFolder,
  uploadImageAction,
  clearUrl,
} from "@/services/ImageServices/ImageActions";
import type { MediaImage } from "../ModelMediaContent/ImageInformation";

/**
 * useMediaBrowser — the single browser core shared by the standalone Media page
 * (mode "manage": browse + create folder + upload + image info) and the modal
 * image picker (mode "select": browse + pick). SCRUM-904 folds the two drifted
 * IndexMedia / IndexModelMedia stacks into this one hook + MediaBrowserView.
 *
 * Design (resolves the reconciliation-audit drift, all best-of-both):
 *  - One robust normalizePath (backslash->slash, collapse //, trim, strip trailing
 *    slash keeping a lone "/") used for BOTH the cache key AND the server dispatch.
 *  - Per-path cache keyed by normalized path. Rendering reads ONLY this cache, never
 *    the shared global s.images.* slots, so a cache miss shows empty (never another
 *    folder's contents — kills the modal's wrong-folder flash).
 *  - Cache-first: a path is fetched once on first visit and served from cache on
 *    revisit (Dirk's decision), with an explicit Refresh to force a re-fetch.
 *  - fetchPath writes folders+pictures TOGETHER, keyed to the path they were fetched
 *    for (getFolders/getPictures now resolve to their data), so a half-resolved fetch
 *    can never cache one path's folders next to another path's pictures.
 *  - Empty folders are cached like any other (no re-fetch on revisit).
 *  - A single path-change effect is the only fetch trigger; an in-flight set dedupes
 *    concurrent/StrictMode double invocations, so each path fetches exactly once.
 */

const PAGE_SIZE = 10;
const PAGE_STEP = 6;

/** A folder entry as the folder listing (or createFolder) returns it. */
type MediaFolder = { name?: string; path?: string };

/** One cached folder listing, keyed by its normalized path. */
type CacheEntry = { folders: MediaFolder[]; pictures: MediaImage[] };

/**
 * The local file picked for upload. AddImage stores the FileReader result as a
 * one-element array, so `secure_url` is a string or a string array.
 */
type UploadDraft = { secure_url?: string | string[]; filename?: string };

type UseMediaBrowserOptions = {
  mode?: "manage" | "select";
  /** folder to open on mount */
  initialPath?: string;
  /** where Home navigates to (default root "") */
  rootPath?: string;
  /** select-mode pick handler */
  onSelect?: (image: MediaImage) => void;
};

function normalizePath(p: unknown): string {
  if (!p) return "";
  const cleaned = String(p)
    .replace(/\\/g, "/")
    .replace(/\/{2,}/g, "/")
    .trim();
  return cleaned.endsWith("/") && cleaned !== "/" ? cleaned.slice(0, -1) : cleaned;
}

export default function useMediaBrowser({
  mode = "manage",
  initialPath = "",
  rootPath = "",
  onSelect,
}: UseMediaBrowserOptions = {}) {
  const dispatch = useAppDispatch();

  const [path, setPath] = useState(() => normalizePath(initialPath));
  const [cache, setCache] = useState<Record<string, CacheEntry>>({});
  const [showElemNumbers, setShowElemNumbers] = useState(PAGE_SIZE);

  // manage-mode local state
  const [showFormFolder, setShowFormFolder] = useState(false);
  const [img, setImg] = useState<UploadDraft>({});
  const [hideLocalImg, setHideLocalImg] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cacheRef = useRef(cache);
  useEffect(() => {
    cacheRef.current = cache;
  }, [cache]);
  const inflightRef = useRef<Set<string>>(new Set());

  const writeEntry = useCallback((key: string, folders: unknown, pictures: unknown) => {
    setCache((prev) => ({
      ...prev,
      [key]: {
        folders: Array.isArray(folders) ? folders : [],
        pictures: Array.isArray(pictures) ? pictures : [],
      },
    }));
  }, []);

  const fetchPath = useCallback(
    async (p: string, { force = false }: { force?: boolean } = {}) => {
      const key = normalizePath(p);
      if (!force && cacheRef.current[key]) return; // cache-first
      if (inflightRef.current.has(key)) return; // dedupe concurrent / StrictMode
      inflightRef.current.add(key);
      try {
        const [folders, pictures] = await Promise.all([
          dispatch(getFolders({ public_id: key })),
          dispatch(getPictures({ folder: key })),
        ]);
        // Only cache a SUCCESSFUL fetch. getFolders/getPictures resolve to undefined
        // on error (having dispatched GET_ERRORS); caching that as an empty folder
        // would make cache-first serve a poisoned empty listing until Refresh/remount.
        // Leaving the path un-cached on failure lets the next navigation retry it.
        if (Array.isArray(folders) && Array.isArray(pictures)) {
          writeEntry(key, folders, pictures);
        }
      } finally {
        inflightRef.current.delete(key);
      }
    },
    [dispatch, writeEntry]
  );

  // The ONLY fetch trigger: mount + every navigation funnels through a path change.
  useEffect(() => {
    fetchPath(path);
  }, [path, fetchPath]);

  useEffect(
    () => () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    },
    []
  );

  const navigate = useCallback((p: string) => {
    setPath(normalizePath(p));
    setShowElemNumbers(PAGE_SIZE);
  }, []);

  const { folders, pictures, currentPath } = useMemo(() => {
    const key = normalizePath(path);
    const entry = cache[key];
    return {
      folders: entry?.folders || [],
      pictures: entry?.pictures || [],
      currentPath: key,
    };
  }, [cache, path]);

  const totalCount = folders.length + pictures.length;
  const visibleCount = Math.min(showElemNumbers, totalCount);

  const onSeeMore = useCallback(() => {
    setShowElemNumbers((n) => Math.min(n + PAGE_STEP, totalCount));
  }, [totalCount]);

  const onHomeBack = useCallback(() => navigate(rootPath), [navigate, rootPath]);

  const onStepBack = useCallback(() => {
    const parts = (currentPath || "").split("/").filter(Boolean);
    navigate(parts.slice(0, parts.length - 1).join("/"));
  }, [navigate, currentPath]);

  const onFolderClick = useCallback(
    (folder: unknown) => navigate((folder as MediaFolder | null | undefined)?.path || ""),
    [navigate]
  );

  const onRefresh = useCallback(() => {
    fetchPath(currentPath, { force: true });
  }, [fetchPath, currentPath]);

  const onImageClick = useCallback(
    (image: MediaImage) => {
      if (onSelect) onSelect(image);
    },
    [onSelect]
  );

  // ---- manage mode: create folder + upload --------------------------------
  const onShowForm = useCallback(() => setShowFormFolder(true), []);
  const onCloseForm = useCallback(() => setShowFormFolder(false), []);

  const onSubmitFolder = useCallback(
    async (folderName: unknown) => {
      const safe = String(folderName || "").trim();
      if (!safe) return;
      const key = normalizePath(path);
      const created = await dispatch(addFolder(normalizePath(`${path}/${safe}`)));
      dispatch(clearFolder());
      if (created?.name) {
        setCache((prev) => {
          const cur = prev[key] || { folders: [], pictures: [] };
          return { ...prev, [key]: { ...cur, folders: [created, ...cur.folders] } };
        });
      }
    },
    [dispatch, path]
  );

  const onImgToUpload = useCallback((nextImg: unknown) => setImg((nextImg as UploadDraft) || {}), []);

  // SCRUM-1594: the info panel saved a new alt for one resource. Patch the cached
  // picture in place (flat `context.alt`, the getImagesByFolder shape) so the panel
  // does not show the stale default until the next Refresh.
  const onAltSaved = useCallback((publicId: string, alt: string) => {
    setCache((prev) => {
      let changed = false;
      const next: typeof prev = {};
      for (const [key, entry] of Object.entries(prev)) {
        const pictures = entry.pictures.map((p) => {
          if (p?.public_id !== publicId) return p;
          changed = true;
          return { ...p, context: { ...(p.context || {}), alt } };
        });
        next[key] = changed ? { ...entry, pictures } : entry;
      }
      return changed ? next : prev;
    });
  }, []);

  const onUploadAction = useCallback(async () => {
    const src = img?.secure_url;
    const filename = img?.filename;
    if (!src || !filename) return;
    const base64 = Array.isArray(src) ? src[0] : src;
    const key = normalizePath(path);
    let uploadPath = normalizePath(`${path}/${filename}`);
    if (uploadPath && !uploadPath.startsWith("/")) uploadPath = `/${uploadPath}`;

    try {
      const uploadedUrl = await dispatch(uploadImageAction(base64, uploadPath));
      if (uploadedUrl) {
        const newImg = { secure_url: uploadedUrl, filename };
        setCache((prev) => {
          const cur = prev[key] || { folders: [], pictures: [] };
          return { ...prev, [key]: { ...cur, pictures: [newImg, ...cur.pictures] } };
        });
        setImg({});
        setHideLocalImg(true);
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setHideLocalImg(false), 500);
      }
    } catch {
      // uploadImageAction rejects into the errors hub (SCRUM-2119). This catch
      // only skips prepending a picture; a second GET_ERRORS would toast twice.
    } finally {
      dispatch(clearUrl());
    }
  }, [dispatch, img, path]);

  return {
    mode,
    path: currentPath,
    folders,
    pictures,
    showElemNumbers: visibleCount,
    totalCount,
    onSeeMore,
    onHomeBack,
    onStepBack,
    onFolderClick,
    onRefresh,
    onImageClick,
    // manage mode
    showFormFolder,
    img,
    hideLocalImg,
    onShowForm,
    onCloseForm,
    onSubmitFolder,
    onImgToUpload,
    onUploadAction,
    onAltSaved,
  };
}
